// src/components/dashboard/TeamDashboard.jsx (最終統合・完成版)

import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, TrendingUp, Award, Target, BarChart3, UserCheck, Zap, Star, AlertCircle, BookOpen, Brain, GitBranch, ArrowRight } from 'lucide-react';
import AbilityRadarChart from '../team/AbilityRadarChart';

import Modal from '../ui/Modal';
import { trizLaws as allTrizLaws } from '../../data/trizLawDependencies';

// SafeIcon コンポーネント（エラーハンドリング付き）
const SafeIcon = ({ IconComponent, className = "", fallbackText = "📊", ...props }) => {
  try {
    return <IconComponent className={className} {...props} />;
  } catch (error) {
    console.warn('Icon rendering failed:', error);
    return <span className={className}>{fallbackText}</span>;
  }
};

const AdvancedTeamDashboard = ({
  userProfiles,
  radarAxes,
  trizLaws
}) => {
  const [selectedLaw, setSelectedLaw] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [analysisView, setAnalysisView] = useState('overview');
  const [sortBy, setSortBy] = useState('score');

  if (!userProfiles) {
    return (
      <div className="p-6 bg-white border border-gray-200 rounded-lg text-center">
        <p className="text-gray-500">チームデータを読み込み中です...</p>
      </div>
    );
  }

  const handleLawClick = (lawId) => {
    const lawData = allTrizLaws[lawId];
    if (lawData) {
      setSelectedLaw({ id: lawId, ...lawData });
    }
  };

  const validateData = useCallback(() => {
    const errors = [];
    if (!userProfiles || Object.keys(userProfiles).length === 0) {
      errors.push('分析対象のユーザープロファイルデータが見つかりません');
    }
    return errors;
  }, [userProfiles]);

  const calculateSkillDistribution = useCallback((members) => {
    const distribution = { beginner: 0, intermediate: 0, advanced: 0, expert: 0 };
    members.forEach(member => {
      const score = member.averageScore || 0;
      if (score < 2) distribution.beginner++;
      else if (score < 3.5) distribution.intermediate++;
      else if (score < 4.5) distribution.advanced++;
      else distribution.expert++;
    });
    return distribution;
  }, []);

  const calculateTeamStrengths = useCallback((members) => {
    const cognitiveStyles = {};
    const allStrengths = [];
    members.forEach(member => {
      if (member.cognitiveStyle) {
        cognitiveStyles[member.cognitiveStyle] = (cognitiveStyles[member.cognitiveStyle] || 0) + 1;
      }
      if (member.strengths && Array.isArray(member.strengths)) {
        allStrengths.push(...member.strengths);
      }
    });
    const strengthCounts = {};
    allStrengths.forEach(strength => {
      strengthCounts[strength] = (strengthCounts[strength] || 0) + 1;
    });
    const topStrengths = Object.entries(strengthCounts)
      .filter(([, count]) => count >= 2)
      .map(([strength, count]) => ({ strength, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    return { cognitiveStyles, topStrengths };
  }, []);

  const calculateSkillGaps = useCallback((members, laws) => {
    if (!laws || Object.keys(laws).length === 0) return [];
    const lawIds = Object.keys(laws);
    const gaps = [];
    lawIds.forEach(lawId => {
      const scores = members.map(member => {
        const mastery = member.trizLawsMastery && member.trizLawsMastery[lawId];
        return mastery && typeof mastery.score === 'number' ? mastery.score : 0;
      }).filter(score => score >= 0);
      if (scores.length === 0) return;
      const averageScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      const strongCount = scores.filter(score => score >= 4).length;
      const coverage = strongCount / members.length;
      let priority = 'low';
      if (averageScore < 2 || coverage < 0.2) priority = 'high';
      else if (averageScore < 3 || coverage < 0.4) priority = 'medium';
      if (priority !== 'low') {
        gaps.push({ lawId, lawName: laws[lawId]?.name || lawId, averageScore, coverage, priority });
      }
    });
    return gaps.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }, []);

  const calculateRealCollaborationMatrix = useCallback((members, laws) => {
    if (!laws || Object.keys(laws).length === 0) return [];
    const lawIds = Object.keys(laws).slice(0, 8); // 表示件数を8件に制限
    return lawIds.map(lawId => {
      const lawName = laws[lawId]?.name || lawId;
      const skillData = members.map(member => {
        const lawMastery = member.trizLawsMastery && member.trizLawsMastery[lawId];
        const score = lawMastery && typeof lawMastery.score === 'number' ? lawMastery.score : 0;
        return { name: member.name, level: score, isStrong: score >= 4, isImproving: score >= 2 && score < 4 };
      });
      const averageLevel = skillData.reduce((sum, data) => sum + data.level, 0) / skillData.length;
      const strongCount = skillData.filter(data => data.isStrong).length;
      return { skill: lawName, lawId, data: skillData, averageLevel, strongCount, coverage: strongCount / members.length };
    });
  }, []);

  const teamAnalysis = useMemo(() => {
    const validationErrors = validateData();
    if (validationErrors.length > 0) {
      return {
        members: [],
        teamStats: { totalMembers: 0, averageTeamScore: 0, totalMasteredLaws: 0, topPerformer: null, skillDistribution: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 }, collaborationMatrix: [], errors: validationErrors, teamStrengths: { cognitiveStyles: {}, topStrengths: [] }, skillGaps: [] }
      };
    }
    const members = Object.values(userProfiles)
      .filter(profile => profile && profile.role !== 'master')
      .map(profile => {
        const uid = profile.id; // 仮。実際のID取得方法に依存
        const memberData = { id: uid, name: profile.username || `ユーザー${uid.slice(0, 6)}`, avatar: '👤', ...profile };
        if (profile.trizLawsMastery && typeof profile.trizLawsMastery === 'object') {
          const trizData = Object.entries(profile.trizLawsMastery);
          const validScores = trizData.map(([, law]) => law && typeof law.score === 'number' ? law.score : 0).filter(score => score >= 0);
          memberData.averageScore = validScores.length > 0 ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length : 0;
          memberData.totalLaws = trizData.length;
          memberData.masteredLaws = validScores.filter(score => score >= 4).length;
          memberData.improvingLaws = validScores.filter(score => score >= 2 && score < 4).length;
          const skillsWithScores = trizData.filter(([, law]) => law && typeof law.score === 'number').sort((a, b) => b[1].score - a[1].score);
          memberData.strongestSkill = skillsWithScores[0] ? { lawId: skillsWithScores[0][0], score: skillsWithScores[0][1].score, name: allTrizLaws[skillsWithScores[0][0]]?.name || skillsWithScores[0][0] } : null;
        } else {
          memberData.averageScore = 0; memberData.totalLaws = 0; memberData.masteredLaws = 0; memberData.improvingLaws = 0; memberData.strongestSkill = null;
        }
        const thinkingValue = profile.neurodiversityProfile?.thinkingStyle?.value;
        if (typeof thinkingValue === 'number') {
          if (thinkingValue > 60) memberData.cognitiveStyle = '発散的思考タイプ';
          else if (thinkingValue < 40) memberData.cognitiveStyle = '収束的思考タイプ';
          else memberData.cognitiveStyle = 'バランス型';
        } else {
          memberData.cognitiveStyle = '未分類';
        }
        if (profile.talentPortfolio?.identifiedTalents) {
          memberData.strengths = profile.talentPortfolio.identifiedTalents;
        } else {
          memberData.strengths = [];
        }
        return memberData;
      });
    const validMembers = members.filter(member => typeof member.averageScore === 'number');
    const teamStats = {
      totalMembers: members.length,
      averageTeamScore: validMembers.length > 0 ? validMembers.reduce((sum, member) => sum + member.averageScore, 0) / validMembers.length : 0,
      totalMasteredLaws: members.reduce((sum, member) => sum + (member.masteredLaws || 0), 0),
      totalImprovingLaws: members.reduce((sum, member) => sum + (member.improvingLaws || 0), 0),
      topPerformer: validMembers.reduce((top, member) => (member.averageScore > (top?.averageScore || 0) ? member : top), null),
      skillDistribution: calculateSkillDistribution(validMembers),
      collaborationMatrix: calculateRealCollaborationMatrix(validMembers, allTrizLaws),
      teamStrengths: calculateTeamStrengths(validMembers),
      skillGaps: calculateSkillGaps(validMembers, allTrizLaws)
    };

    // Calculate Radar Data
    let radarData = [];
    if (radarAxes && validMembers.length > 0) {
      radarData = Object.entries(radarAxes).map(([axisKey, axisDef]) => {
        // Simple average for now as detailed mapping is complex
        const totalScore = validMembers.reduce((sum, m) => sum + (m.averageScore || 0), 0);
        const avg = validMembers.length ? totalScore / validMembers.length : 0;
        return {
          subject: axisDef.name || axisKey,
          A: avg,
          fullMark: 5
        };
      });
    }

    return { members, teamStats, radarData };
  }, [userProfiles, radarAxes, validateData, calculateSkillDistribution, calculateRealCollaborationMatrix, calculateTeamStrengths, calculateSkillGaps]);

  const sortedMembers = useMemo(() => {
    const members = [...teamAnalysis.members];
    switch (sortBy) {
      case 'score': return members.sort((a, b) => (b.averageScore || 0) - (a.averageScore || 0));
      case 'name': return members.sort((a, b) => a.name.localeCompare(b.name));
      case 'laws': return members.sort((a, b) => (b.masteredLaws || 0) - (a.masteredLaws || 0));
      default: return members;
    }
  }, [teamAnalysis.members, sortBy]);

  const MemberCard = ({ member, isSelected, onClick }) => {
    const masteryRate = member.totalLaws > 0 ? ((member.masteredLaws || 0) / member.totalLaws) * 100 : 0;
    return (
      <div className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50 shadow-lg scale-105' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'}`} onClick={() => onClick(member)} >
        <div className="flex items-center gap-3 mb-3">
          <div className="text-2xl">{member.avatar}</div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800">{member.name}</h3>
            <p className="text-sm text-gray-500"> 平均スコア: {(member.averageScore || 0).toFixed(1)}/5.0 </p>
            {member.cognitiveStyle && (<p className="text-xs text-blue-600 font-medium">{member.cognitiveStyle}</p>)}
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">習得/改善中</span>
            <span className="font-semibold text-blue-600"> {member.masteredLaws || 0}/{member.improvingLaws || 0} </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full" style={{ width: `${masteryRate}%` }} ></div>
          </div>
          {member.strongestSkill && (<div className="text-xs text-green-600"> 💪 強み: {member.strongestSkill.name} ({member.strongestSkill.score.toFixed(1)}) </div>)}
        </div>
      </div>
    );
  };

  const SkillDistributionChart = () => {
    const { skillDistribution } = teamAnalysis.teamStats;
    const total = Object.values(skillDistribution).reduce((sum, count) => sum + count, 0);
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <SafeIcon IconComponent={BarChart3} className="w-5 h-5 text-blue-600" />
          チームスキル分布
        </h3>
        <div className="space-y-4">
          {Object.entries(skillDistribution).map(([level, count]) => {
            const percentage = total > 0 ? (count / total) * 100 : 0;
            const levelColors = { beginner: 'bg-red-400', intermediate: 'bg-yellow-400', advanced: 'bg-blue-400', expert: 'bg-green-400' };
            const levelLabels = { beginner: '初級者 (0-2)', intermediate: '中級者 (2-3.5)', advanced: '上級者 (3.5-4.5)', expert: 'エキスパート (4.5-5)' };
            return (
              <div key={level} className="flex items-center gap-3">
                <div className="w-24 text-sm font-medium text-gray-600"> {levelLabels[level]} </div>
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div className={`${levelColors[level]} h-3 rounded-full`} style={{ width: `${percentage}%` }} ></div>
                </div>
                <div className="w-16 text-sm font-semibold text-gray-700"> {count}人 ({percentage.toFixed(1)}%) </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TeamOverviewStats = () => {
    const { teamStats } = teamAnalysis;
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><SafeIcon IconComponent={Users} className="w-5 h-5" /> <span className="text-sm font-medium">チームメンバー</span></div>
          <div className="text-2xl font-bold">{teamStats.totalMembers}</div>
          <div className="text-xs opacity-80">人</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><SafeIcon IconComponent={TrendingUp} className="w-5 h-5" /> <span className="text-sm font-medium">平均スコア</span></div>
          <div className="text-2xl font-bold">{(teamStats.averageTeamScore || 0).toFixed(1)}</div>
          <div className="text-xs opacity-80">/5.0</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><SafeIcon IconComponent={Award} className="w-5 h-5" /> <span className="text-sm font-medium">習得済み法則</span></div>
          <div className="text-2xl font-bold">{teamStats.totalMasteredLaws || 0}</div>
          <div className="text-xs opacity-80">法則</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2"><SafeIcon IconComponent={Star} className="w-5 h-5" /> <span className="text-sm font-medium">トップパフォーマー</span></div>
          <div className="text-lg font-bold truncate"> {teamStats.topPerformer ? teamStats.topPerformer.name : 'N/A'} </div>
          <div className="text-xs opacity-80"> {teamStats.topPerformer ? `${teamStats.topPerformer.averageScore.toFixed(1)}/5.0` : '-'} </div>
        </div>
      </div>
    );
  };

  if (teamAnalysis.teamStats.errors && teamAnalysis.teamStats.errors.length > 0) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-3">
          <SafeIcon IconComponent={AlertCircle} className="w-6 h-6 text-red-600" />
          <div>
            <h2 className="text-lg font-bold text-red-800">データエラー</h2>
            <div className="text-red-700"> {teamAnalysis.teamStats.errors.map((error, index) => (<p key={index}>• {error}</p>))} </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <SafeIcon IconComponent={UserCheck} className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-lg font-bold text-green-800"> 🎉 高度チーム分析システム稼働中！ </h2>
              <p className="text-green-700"> 実際のユーザーデータ（{teamAnalysis.members.length}名）を使用した本格的な分析です。 </p>
            </div>
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold"> LIVE </span>
          </div>
        </div>
        <div className="flex bg-gray-100 p-1 rounded-lg w-fit">
          {[{ id: 'overview', label: '概要', Icon: BarChart3 }, { id: 'skills', label: 'スキル分析', Icon: Target }, { id: 'collaboration', label: '協働分析', Icon: Users }].map(tab => (
            <button key={tab.id} onClick={() => setAnalysisView(tab.id)} className={`px-4 py-2 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${analysisView === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`} >
              <SafeIcon IconComponent={tab.Icon} className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
        <TeamOverviewStats />
        {analysisView === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <SafeIcon IconComponent={Users} className="w-5 h-5 text-blue-600" />
                  チームメンバー ({teamAnalysis.members.length}人)
                </h3>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-sm border rounded-md px-2 py-1" >
                  <option value="score">スコア順</option>
                  <option value="name">名前順</option>
                  <option value="laws">習得法則順</option>
                </select>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {sortedMembers.map(member => (<MemberCard key={member.id} member={member} isSelected={selectedMember && selectedMember.id === member.id} onClick={setSelectedMember} />))}
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <SafeIcon IconComponent={Target} className="w-5 h-5 text-blue-600" />
                  チームバランス診断
                </h3>
                <AbilityRadarChart data={teamAnalysis.radarData || []} />
              </div>
              <SkillDistributionChart />
            </div>
          </div>
        )}
        {analysisView === 'skills' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <SafeIcon IconComponent={Target} className="w-5 h-5 text-blue-600" />
                スキル別チーム力
              </h3>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {teamAnalysis.teamStats.collaborationMatrix.map((item, index) => (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <button
                        onClick={() => handleLawClick(item.lawId)}
                        className="font-medium text-gray-800 hover:text-blue-600 hover:underline cursor-pointer text-left"
                      >
                        {item.skill}
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">強い人: {item.strongCount}人</span>
                        <span className={`text-xs px-2 py-1 rounded ${item.coverage >= 0.5 ? 'bg-green-100 text-green-800' : item.coverage >= 0.3 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                          カバー率: {(item.coverage * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${item.coverage >= 0.5 ? 'bg-green-400' : item.coverage >= 0.3 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${item.coverage * 100}%` }} ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <SafeIcon IconComponent={AlertCircle} className="w-5 h-5 text-orange-600" />
                スキルギャップ分析
              </h3>
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {teamAnalysis.teamStats.skillGaps.length > 0 ? (
                  teamAnalysis.teamStats.skillGaps.map((gap, index) => (
                    <div key={index} className={`p-3 border-l-4 rounded ${gap.priority === 'high' ? 'border-red-500 bg-red-50' : gap.priority === 'medium' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'}`} >
                      <div className="flex justify-between items-center">
                        <button
                          onClick={() => handleLawClick(gap.lawId)}
                          className="font-medium text-gray-800 hover:text-blue-600 hover:underline cursor-pointer text-left"
                        >
                          {gap.lawName}
                        </button>
                        <span className={`text-xs px-2 py-1 rounded ${gap.priority === 'high' ? 'bg-red-200 text-red-800' : gap.priority === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'}`}>
                          {gap.priority === 'high' ? '高優先度' : gap.priority === 'medium' ? '中優先度' : '低優先度'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        平均スコア: {gap.averageScore.toFixed(1)}/5.0 | カバー率: {(gap.coverage * 100).toFixed(0)}%
                      </p>
                    </div>
                  ))
                ) : (<p className="text-gray-500 text-center py-4"> 🎉 スキルギャップは検出されませんでした！ </p>)}
              </div>
            </div>
          </div>
        )}
        {analysisView === 'collaboration' && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <SafeIcon IconComponent={Zap} className="w-5 h-5 text-blue-600" />
              チーム強み・多様性分析
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">認知スタイル分布</h4>
                <div className="space-y-2">
                  {Object.entries(teamAnalysis.teamStats.teamStrengths.cognitiveStyles).map(([style, count]) => (
                    <div key={style} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <span className="text-sm font-medium">{style}</span>
                      <span className="text-sm text-blue-600 font-semibold">{count}人</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-3">チーム共通強み</h4>
                <div className="space-y-2">
                  {teamAnalysis.teamStats.teamStrengths.topStrengths.map(({ strength, count }, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-green-50 rounded">
                      <span className="text-sm font-medium">{strength}</span>
                      <span className="text-sm text-green-600 font-semibold">{count}人</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedMember && (
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">{selectedMember.avatar}</span>
              {selectedMember.name} の詳細分析
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">総合スコア</h4>
                <p className="text-2xl font-bold text-blue-600"> {(selectedMember.averageScore || 0).toFixed(1)}/5.0 </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">習得法則数</h4>
                <p className="text-2xl font-bold text-green-600"> {selectedMember.masteredLaws || 0} </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-semibold text-purple-800 mb-2">最強スキル</h4>
                <p className="text-sm font-bold text-purple-600"> {selectedMember.strongestSkill ? selectedMember.strongestSkill.name : 'データなし'} </p>
                {selectedMember.strongestSkill && (<p className="text-xs text-purple-500"> {selectedMember.strongestSkill.score.toFixed(1)}/5.0 </p>)}
              </div>
              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">認知スタイル</h4>
                <p className="text-sm font-bold text-orange-600"> {selectedMember.cognitiveStyle || 'データなし'} </p>
              </div>
            </div>
            {selectedMember.strengths && selectedMember.strengths.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">個人の強み</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedMember.strengths.map((strength, index) => (<span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full" > {strength} </span>))}
                </div>
              </div>
            )}
          </div>
        )}
      </div >

      <Modal
        isOpen={!!selectedLaw}
        onClose={() => setSelectedLaw(null)}
        title={selectedLaw?.name}
      >
        {selectedLaw && (
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">概要</h4>
              <p className="text-gray-700">{selectedLaw.description}</p>
            </div>

            {selectedLaw.simpleDescription && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">身近な例で言うと...</h4>
                <div className="bg-blue-50 border-l-4 border-blue-300 p-3">
                  <p className="text-gray-800">{selectedLaw.simpleDescription}</p>
                </div>
              </div>
            )}

            {selectedLaw.trizOrigin && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2"><GitBranch className="w-4 h-4 text-green-600" />TRIZ 発明原理との関連</h4>
                <div className="bg-green-50 border-l-4 border-green-300 p-3">
                  <p className="text-sm text-gray-800">{selectedLaw.trizOrigin}</p>
                </div>
              </div>
            )}

            {selectedLaw.psychologyOrigin && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2"><Brain className="w-4 h-4 text-yellow-600" />発達心理学との関連</h4>
                <div className="bg-yellow-50 border-l-4 border-yellow-300 p-3">
                  <p className="text-sm text-gray-800">{selectedLaw.psychologyOrigin}</p>
                </div>
              </div>
            )}

            {selectedLaw.nakabaseAspect && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4 text-gray-600" />富永仲基の思想との関連</h4>
                <div className="bg-gray-100 border-l-4 border-gray-300 p-3">
                  <p className="text-sm text-gray-800">{selectedLaw.nakabaseAspect}</p>
                </div>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200">
              <Link
                to="/framework-details"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium inline-flex items-center gap-1"
                onClick={() => setSelectedLaw(null)}
              >
                理論的背景についてさらに詳しく
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AdvancedTeamDashboard;